import React from 'react';
import { Chart } from 'chart.js';

import { MainAnalyticsChartContext } from '../../main-analytics.store';
import { MainAnalyticsChartDataItem } from '../../main-analytics.types';
import { formatChartNumber } from './chart.constants';
import { registerMainAnalyticsCharts } from './register-main-analytics-charts';

type ActivityLineChartProps = {
   data: MainAnalyticsChartDataItem[];
   context: MainAnalyticsChartContext;
};

export const ActivityLineChart = ({ data, context }: ActivityLineChartProps) => {
   const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

   React.useEffect(() => {
      registerMainAnalyticsCharts();

      const canvas = canvasRef.current;
      if (!canvas) {
         return undefined;
      }

      const activeKeys: Record<MainAnalyticsChartContext, string[]> = {
         cv: ['cv', 'simple_cv'],
         simple_cv: ['simple_cv', 'cv'],
         registrations: ['registrations'],
         jobs: ['job', 'job_apply'],
      };
      const selected = new Set(activeKeys[context]);

      const chart = new Chart(canvas, {
         type: 'line',
         data: {
            labels: data.map((item) => item.name),
            datasets: [
               {
                  label: 'Створено резюме',
                  data: data.map((item) => item.cv),
                  borderColor: '#1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  pointRadius: 3,
                  tension: 0.35,
                  fill: selected.has('cv'),
                  hidden: !selected.has('cv'),
                  yAxisID: 'y',
               },
               {
                  label: 'Короткі анкети',
                  data: data.map((item) => item.simple_cv),
                  borderColor: '#dd6b20',
                  backgroundColor: 'rgba(221, 107, 32, 0.08)',
                  pointRadius: 3,
                  tension: 0.35,
                  fill: selected.has('simple_cv'),
                  hidden: !selected.has('simple_cv'),
                  yAxisID: 'y1',
               },
               {
                  label: 'Реєстрації',
                  data: data.map((item) => item.registrations),
                  borderColor: '#2c9c9c',
                  backgroundColor: 'rgba(44, 156, 156, 0.08)',
                  pointRadius: 3,
                  tension: 0.35,
                  fill: selected.has('registrations'),
                  hidden: !selected.has('registrations'),
                  yAxisID: 'y',
               },
               {
                  label: 'Всього вакансій',
                  data: data.map((item) => item.job),
                  borderColor: '#2f9e44',
                  backgroundColor: 'rgba(47, 158, 68, 0.08)',
                  pointRadius: 3,
                  tension: 0.35,
                  fill: selected.has('job'),
                  hidden: !selected.has('job'),
                  yAxisID: 'y1',
               },
               {
                  label: 'Відгуки',
                  data: data.map((item) => item.job_apply),
                  borderColor: '#8b5cf6',
                  backgroundColor: 'rgba(139, 92, 246, 0.08)',
                  pointRadius: 3,
                  tension: 0.35,
                  fill: selected.has('job_apply'),
                  hidden: !selected.has('job_apply'),
                  yAxisID: 'y1',
               },
            ],
         },
         options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
               mode: 'index',
               intersect: false,
            },
            plugins: {
               legend: {
                  position: 'bottom',
                  labels: {
                     usePointStyle: true,
                     boxWidth: 8,
                     color: '#202020',
                  },
               },
               tooltip: {
                  callbacks: {
                     label: (item) =>
                        `${item.dataset.label}: ${formatChartNumber(Number(item.raw ?? 0))}`,
                  },
               },
            },
            scales: {
               x: {
                  grid: {
                     color: '#e1e5ea',
                  },
                  ticks: {
                     color: '#555555',
                  },
               },
               y: {
                  beginAtZero: true,
                  position: 'left',
                  grid: {
                     color: '#d9dde3',
                  },
                  ticks: {
                     color: '#555555',
                  },
                  title: {
                     display: true,
                     text: 'Активність',
                  },
               },
               y1: {
                  beginAtZero: true,
                  position: 'right',
                  grid: {
                     drawOnChartArea: false,
                  },
                  ticks: {
                     color: '#555555',
                  },
               },
            },
         },
      });

      return () => chart.destroy();
   }, [context, data]);

   return <canvas ref={canvasRef} role="img" aria-label="Динаміка активності" />;
};
